import { useEffect, useMemo } from "react";
import { getRequiredGoogleFonts, loadGoogleFont, type FontConfig } from "@/utils/fontLoader";
import { useSiteConfigSafe } from "@/contexts/SiteConfigContext";
import { resolveTypography } from "@/utils/typographyResolver";

interface ExtendedTypographyConfig {
  bodyFont?: string;
  bodyFontWeights?: string[];
  bodyFontSubsets?: string[];
}

interface ConfigWithExtendedTypography {
  typography?: ExtendedTypographyConfig;
}

const SECTIONS = [
  "hero",
  "about",
  "experience",
  "skills",
  "projects",
  "gallery",
  "timeline",
  "contact",
  "social",
  "testimonials",
  "faq",
  "services",
  "features",
  "pricing",
  "team",
  "cta",
] as const;

export function FontPreloader() {
  const siteConfigContext = useSiteConfigSafe();
  const config = siteConfigContext?.config;

  // Collect all fonts that need to be loaded using the resolver
  const fontsToLoad = useMemo(() => {
    if (!config) return [];

    const fonts = new Map<string, { weights: Set<string>; subsets: Set<string> }>();

    const addFont = (family: string, weights: string[], subsets: string[]) => {
      if (!family) return;

      const existing = fonts.get(family);
      if (existing) {
        weights.forEach(w => existing.weights.add(w));
        subsets.forEach(s => existing.subsets.add(s));
      } else {
        fonts.set(family, {
          weights: new Set(weights),
          subsets: new Set(subsets),
        });
      }
    };

    // Load global typography fonts
    const t = (config as ConfigWithExtendedTypography).typography;
    if (t?.bodyFont) {
      addFont(t.bodyFont, t.bodyFontWeights || ["400", "500"], t.bodyFontSubsets || ["latin"]);
    }

    // Load section-specific fonts using the resolver
    SECTIONS.forEach((sectionName) => {
      const sectionConfig = (config as any)[sectionName];

      // Resolve title typography
      const titleTypography = resolveTypography(config, sectionName, "title", sectionConfig);
      if (titleTypography.fontFamily) {
        addFont(titleTypography.fontFamily, titleTypography.weights, titleTypography.subsets);
      }

      // Resolve subtitle typography
      const subtitleTypography = resolveTypography(config, sectionName, "subtitle", sectionConfig);
      if (subtitleTypography.fontFamily) {
        addFont(subtitleTypography.fontFamily, subtitleTypography.weights, subtitleTypography.subsets);
      }

      // Resolve body typography
      const bodyTypography = resolveTypography(config, sectionName, "body", sectionConfig);
      if (bodyTypography.fontFamily) {
        addFont(bodyTypography.fontFamily, bodyTypography.weights, bodyTypography.subsets);
      }
    });

    // Also get any additional fonts from deep config walk
    const requiredFonts = getRequiredGoogleFonts(config as FontConfig);
    requiredFonts.forEach(({ family, weights, subsets }) => {
      addFont(family, weights || [], subsets || []);
    });

    return Array.from(fonts.entries()).map(([family, { weights, subsets }]) => ({
      family,
      weights: Array.from(weights).sort(),
      subsets: Array.from(subsets).sort(),
    }));
  }, [config]);

  useEffect(() => {
    // Load all collected fonts
    fontsToLoad.forEach(({ family, weights, subsets }) => {
      loadGoogleFont(family, weights, subsets);
    });
  }, [fontsToLoad]);

  return null;
}
